import { Transformation } from 'cloudinary-core';
import clsx from 'clsx';
import React, { ImgHTMLAttributes } from 'react';

import { Image, Video, Pdf } from 'generated/graphql';

import { getImageUrl } from 'lib/cloudinary';
import { isVideo } from 'lib/media';
import { ImageSizeThreshold, ImageSizeValues, imageThresholds as defaultThresholds, imageSizes as availableSizes } from 'lib/mediaThresholds';

import { useStyles } from './styles';

export type ComponentProps = {
	id: string;
	media: Image | Video | Pdf;
	placeholderColor?: string;
	label?: React.ReactElement;
	containerClassName?: string;
	imgClassName?: string;
	onClick?: React.MouseEventHandler;
	sizes?: ImageSizeValues;
	mobileBreakpoint?: number;
	imageTresholds?: ImageSizeThreshold[];
	imageAttributes?: Omit<ImgHTMLAttributes<HTMLImageElement>, 'className' | 'src' | 'onLoad' | 'srcSet' | 'sizes' | 'onClick'>;
};

const MediaImage = (props: ComponentProps) => {
	const {
		id,
		media,
		placeholderColor = '',
		label = null,
		sizes: imageSizes = availableSizes.default,
		mobileBreakpoint = 959,
		imageTresholds = defaultThresholds,
		imageAttributes = {},
		containerClassName = '',
		imgClassName = '',
		onClick
	} = props;

	const classes = useStyles({ placeholderColor });

	const [imgLoaded, setImgLoaded] = React.useState(false);

	const ratio = media.width / media.height;

	const defaultOptions: Transformation.Options = React.useMemo(
		() => ({
			quality: 'auto:good',
			width: ratio > 1.0 ? 2000 : 1000,
			crop: 'scale',
			version: media.cloudinaryVersion.toString(),
			fetchFormat: 'auto',
			resource_type: isVideo(media) ? 'video' : 'image'
		}),
		[media, ratio]
	);

	const srcSet = React.useMemo(
		() =>
			imageTresholds.map(
				threshold =>
					`${getImageUrl(media.cloudinaryId, {
						...defaultOptions,
						width: threshold.cloudinarySize,
						crop: 'scale'
					})} ${threshold.imageSize}w`
			),
		[imageTresholds, media, defaultOptions]
	);

	const sizes = React.useMemo(() => {
		const mobileSize = imageSizes[0] * ratio;
		const desktopSize = imageSizes[1] * ratio;
		return `(max-width: ${mobileBreakpoint}px) ${Math.min(100, mobileSize)}vw, ${Math.min(100, desktopSize)}vw`;
	}, [imageSizes, mobileBreakpoint, ratio]);

	const onLoad = React.useCallback(() => {
		setImgLoaded(true);
	}, []);

	return (
		<div id={`${id}_container`} className={clsx(classes.imageRoot, containerClassName)} onClick={onClick}>
			<div
				id={`${id}_placeholder`}
				data-testid={`${id}_placeholder`}
				data-hidden={imgLoaded}
				className={clsx(classes.previewMediaPlaceholder, { [classes.previewMediaPlaceholderHidden]: imgLoaded })}
			/>
			<img
				id={`${id}`}
				className={clsx(classes.image, imgClassName)}
				src={getImageUrl(media.cloudinaryId, defaultOptions)}
				alt={media.name}
				width={media.width}
				height={media.height}
				srcSet={srcSet?.join(',')}
				sizes={sizes}
				onLoad={onLoad}
				{...imageAttributes}
			/>
			<div className={classes.linearOverlay} />
			{label}
		</div>
	);
};

export default React.memo(MediaImage);
